// Shared month-column math for horizontal timeline views (Task Planning's
// Jalons/Tâches lanes, Gérance's project calendar). A "slot" is one visible
// calendar month; positions are expressed in px along a horizontal axis of
// `slots.length * monthWidth`, day-of-month interpolated linearly within it.

export interface MonthSlot { year: number; month: number; } // month: 0-11

export function daysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

export function monthKey(m: MonthSlot): string {
  return `${m.year}-${m.month}`;
}

export function buildMonthRange(monthsBefore: number, monthsAfter: number): MonthSlot[] {
  const now = new Date();
  const arr: MonthSlot[] = [];
  for (let i = -monthsBefore; i <= monthsAfter; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
    arr.push({ year: d.getFullYear(), month: d.getMonth() });
  }
  return arr;
}

/**
 * Px offset of a 'YYYY-MM-DD' date along the timeline. Dates before the first
 * visible month clamp to 0 (left edge); dates after the last visible month
 * clamp to the full timeline width (right edge) — so out-of-range bars still
 * render, pinned against the edge they overflow, rather than disappearing.
 */
export function dateOffsetPx(dateStr: string, months: MonthSlot[], monthWidth: number): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(dateStr);
  if (!m) return null;
  const year = Number(m[1]), month = Number(m[2]) - 1, day = Number(m[3]);
  const first = months[0], last = months[months.length - 1];
  if (year < first.year || (year === first.year && month < first.month)) return 0;
  if (year > last.year || (year === last.year && month > last.month)) return months.length * monthWidth;
  const idx = months.findIndex(x => x.year === year && x.month === month);
  return idx * monthWidth + ((day - 1) / daysInMonth(year, month)) * monthWidth;
}

/**
 * Greedily assigns each bar the first lane whose previous bar has already
 * ended, so bars that overlap in time (or that both clamp to the same edge
 * when their real dates fall outside the visible window) stack into separate
 * lanes instead of rendering on top of one another. Shared by every
 * horizontal timeline that can have concurrent bars on one row (Gérance's
 * project calendar, the per-person resource comparison).
 */
export function packLanes<T extends { left: number; width: number }>(bars: T[]): (T & { lane: number })[] {
  const sorted = [...bars].sort((a, b) => a.left - b.left);
  const laneEnds: number[] = [];
  return sorted.map(bar => {
    let lane = laneEnds.findIndex(end => end <= bar.left);
    if (lane === -1) { lane = laneEnds.length; laneEnds.push(bar.left + bar.width); }
    else laneEnds[lane] = bar.left + bar.width;
    return { ...bar, lane };
  });
}
